<script lang="ts" setup>
import { computed, toRefs } from "vue";
import { getUniqueId } from "@/shared/helper/uniqueId";
import type { IconName } from "@/shared/@types/IconName";
import Icon from "../Icon/Icon.vue";
import TextButton from "../TextButton/TextButton.vue";

// IMPORTANT: This component may only be used in combination with the elements input, select and textarea.
const props = withDefaults(
  defineProps<{
    label: string;
    id: string;
    size?: "s" | "m" | "l";
    prependIcon?: IconName;
    isLabelHidden?: boolean;
    isRequired?: boolean;
    isDisabled?: boolean;
    infoButtonCaption?: string;
    hasInfoButton?: boolean;
    hint?: string;
    hasLinkedHint?: boolean;
    errorMessages?: Array<string>;
    isErrored?: boolean;
    hideErrorAppendIcon?: boolean;
  }>(),
  {
    size: "m",
    errorMessages: () => [],
  },
);

const { id: inputFieldId, errorMessages, isErrored } = toRefs(props);

defineEmits<{
  (e: "openHint"): void;
  (e: "showInformation"): void;
}>();

const errored = computed(
  () => errorMessages.value?.length > 0 || isErrored.value,
);

const errorMessageId = getUniqueId("input-group-error");
const inputBindings = computed(() => {
  return {
    "aria-invalid": errored.value || undefined,
    "aria-errormessage":
      errorMessages.value?.length > 0 ? errorMessageId : undefined,
  };
});
</script>

<template>
  <div
    class="input-group"
    :data-size="size"
    :data-errored="errored"
    :data-required="isRequired"
    :data-disabled="isDisabled"
    :data-has-append="!!$slots.append"
    :data-has-prepend="!!prependIcon"
    :data-label-hidden="isLabelHidden"
    :data-has-linked-hint="hasLinkedHint || undefined"
  >
    <div class="input-group--label-box">
      <label
        class="input-group--label"
        :for="inputFieldId"
      >
        {{ label }}
      </label>
      <slot name="label-append"></slot>
    </div>
    <div class="input-group--field">
      <label
        v-if="prependIcon"
        class="input-group--prepend"
        :for="inputFieldId"
      >
        <Icon :icon="prependIcon" />
      </label>
      <slot :input-bindings="inputBindings"></slot>
      <label
        v-if="!!$slots.append || hasInfoButton"
        class="input-group--append"
        :for="inputFieldId"
      >
        <TextButton
          v-if="hasInfoButton"
          :caption="infoButtonCaption || $t('shared.action.show.information')"
          color="black"
          icon="help"
          :icon-size="24"
          icon-position="icon-only"
          @click="$emit('showInformation')"
        />
        <slot name="append"></slot>
      </label>
      <div
        v-else-if="errored && !hideErrorAppendIcon"
        class="input-group--append"
      >
        <div class="input-group--append-error-icon">
          <Icon icon="errorCircleRounded" />
        </div>
      </div>
    </div>
    <template v-if="hint">
      <button
        v-if="hasLinkedHint"
        type="button"
        class="input-group--hint"
        @click="$emit('openHint')"
      >
        {{ hint }}
      </button>
      <span
        v-else
        class="input-group--hint"
      >
        {{ hint }}
      </span>
    </template>
    <slot
      name="errorMessage"
      :error-message-id="errorMessageId"
    >
      <span
        v-if="!!errorMessages[0]"
        :id="errorMessageId"
        class="input-group--error-message"
      >
        {{ errorMessages[0] }}
      </span>
    </slot>
  </div>
</template>

<style scoped>
@import "InputGroup.css";
</style>
